import type { MailTransport } from '@bm/contracts';
import { Field, Input } from './ui';

export interface MailFieldsState {
  transport: MailTransport;
  host: string;
  port: number;
  secure: boolean;
  user: string;
  password: string;
  apiUrl: string;
  apiKey: string;
  tenantId: string;
  clientId: string;
  clientSecret: string;
  clientSecretExpiresAt: string;
  graphSaveToSentItems: boolean;
  fromAddress: string;
  fromName: string;
}

export const EMPTY_MAIL_FIELDS: MailFieldsState = {
  transport: 'smtp',
  host: '',
  port: 587,
  secure: false,
  user: '',
  password: '',
  apiUrl: '',
  apiKey: '',
  tenantId: '',
  clientId: '',
  clientSecret: '',
  clientSecretExpiresAt: '',
  graphSaveToSentItems: true,
  fromAddress: '',
  fromName: '',
};

/** Ob `clientSecretExpiresAt` (ISO-Datum) in weniger als 30 Tagen abläuft
 * oder schon abgelaufen ist — reine UI-Warnung, kein Versandverhalten. */
function secretExpiryWarning(iso: string): string | null {
  if (!iso) return null;
  const expires = new Date(iso);
  if (Number.isNaN(expires.getTime())) return null;
  const daysLeft = Math.ceil((expires.getTime() - Date.now()) / (24 * 60 * 60 * 1000));
  if (daysLeft < 0) return 'Dieser Clientschlüssel ist bereits abgelaufen.';
  if (daysLeft <= 30) return `Dieser Clientschlüssel läuft in ${daysLeft} Tag(en) ab.`;
  return null;
}

/** The "wie wird verschickt" half of both Verein/Plattform › SMTP Server
 * pages — ein Versandweg-Umschalter (klassisches SMTP, die HTTP-API eines
 * Anbieters wie Postal, https://postalserver.io, oder Microsoft 365 über die
 * Graph-API) plus die Felder für den jeweils gewählten Weg. Als eigene
 * Komponente ausgelagert, damit `PlatformMailPage.tsx`/`OrgMailPage.tsx` nie
 * auseinanderlaufen können — siehe docs/mail.md. */
export function MailConnectionFields({
  value,
  onChange,
  hasPassword,
  hasApiKey,
  hasClientSecret,
  disabled,
}: {
  value: MailFieldsState;
  onChange: (next: MailFieldsState) => void;
  hasPassword: boolean;
  hasApiKey: boolean;
  hasClientSecret: boolean;
  disabled?: boolean;
}) {
  const set = <K extends keyof MailFieldsState>(key: K, v: MailFieldsState[K]) => onChange({ ...value, [key]: v });

  // Ein Fixen `grid-cols-2` ließ lange Labels ("Verzeichnis-ID (Tenant ID)",
  // "Geheimer Clientschlüssel …") auf Handy-Breite in 2-3 Zeilen umbrechen —
  // viel vertikaler Leerraum, wirkte gestaucht statt kompakt (§ Nutzeranfrage
  // „Felder kompakter … für Mobiltelefone"). Auf Mobile (`grid-cols-1`) steht
  // jedes Feld in voller Breite mit einzeiligem Label, ab `sm:` wie zuvor
  // zweispaltig nebeneinander.
  const fieldGrid = 'grid grid-cols-1 gap-3 sm:grid-cols-2';

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-4">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="radio"
            name="mail-transport"
            checked={value.transport === 'smtp'}
            disabled={disabled}
            onChange={() => set('transport', 'smtp')}
          />
          SMTP
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="radio"
            name="mail-transport"
            checked={value.transport === 'api'}
            disabled={disabled}
            onChange={() => set('transport', 'api')}
          />
          HTTP-API (z. B. Postal)
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="radio"
            name="mail-transport"
            checked={value.transport === 'graph'}
            disabled={disabled}
            onChange={() => set('transport', 'graph')}
          />
          Microsoft 365
        </label>
      </div>

      {value.transport === 'smtp' ? (
        <div className={fieldGrid}>
          <Field label="Host">
            <Input
              value={value.host}
              onChange={(e) => set('host', e.target.value)}
              placeholder="smtp.example.com"
              disabled={disabled}
            />
          </Field>
          <Field label="Port">
            <Input
              type="number"
              min={1}
              max={65535}
              value={value.port}
              onChange={(e) => set('port', Number(e.target.value))}
              disabled={disabled}
            />
          </Field>
          <Field label="Benutzername">
            <Input value={value.user} onChange={(e) => set('user', e.target.value)} disabled={disabled} />
          </Field>
          <Field label={hasPassword ? 'Passwort (gespeichert — leer lassen zum Beibehalten)' : 'Passwort'}>
            <Input
              type="password"
              value={value.password}
              onChange={(e) => set('password', e.target.value)}
              placeholder={hasPassword ? '••••••••' : ''}
              disabled={disabled}
            />
          </Field>
          <label className="sm:col-span-2 flex items-center gap-2 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={value.secure}
              onChange={(e) => set('secure', e.target.checked)}
              disabled={disabled}
            />
            Verschlüsselte Verbindung (TLS/SSL) — meist an für Port 465, aus für 587/25
          </label>
        </div>
      ) : value.transport === 'api' ? (
        <div className={fieldGrid}>
          <Field label="Postal-Server-Adresse">
            <Input
              value={value.apiUrl}
              onChange={(e) => set('apiUrl', e.target.value)}
              placeholder="https://postal.example.com"
              disabled={disabled}
            />
          </Field>
          <Field label={hasApiKey ? 'API-Schlüssel (gespeichert — leer lassen zum Beibehalten)' : 'API-Schlüssel'}>
            <Input
              type="password"
              value={value.apiKey}
              onChange={(e) => set('apiKey', e.target.value)}
              placeholder={hasApiKey ? '••••••••' : ''}
              disabled={disabled}
            />
          </Field>
          <p className="sm:col-span-2 text-xs text-slate-500">
            Server-Adresse ohne Pfad (z.&nbsp;B. https://postal.example.com) — der API-Schlüssel steht in
            Postal beim jeweiligen Server unter „Credentials".
          </p>
        </div>
      ) : (
        <div className={fieldGrid}>
          <Field label="Verzeichnis-ID (Tenant ID)">
            <Input value={value.tenantId} onChange={(e) => set('tenantId', e.target.value)} disabled={disabled} />
          </Field>
          <Field label="Anwendungs-ID (Client ID)">
            <Input value={value.clientId} onChange={(e) => set('clientId', e.target.value)} disabled={disabled} />
          </Field>
          <Field
            label={hasClientSecret ? 'Geheimer Clientschlüssel (gespeichert — leer lassen zum Beibehalten)' : 'Geheimer Clientschlüssel (Wert)'}
          >
            <Input
              type="password"
              value={value.clientSecret}
              onChange={(e) => set('clientSecret', e.target.value)}
              placeholder={hasClientSecret ? '••••••••' : ''}
              disabled={disabled}
            />
          </Field>
          <Field label="Schlüssel gültig bis (optional)">
            <Input
              type="date"
              value={value.clientSecretExpiresAt}
              onChange={(e) => set('clientSecretExpiresAt', e.target.value)}
              disabled={disabled}
            />
          </Field>
          {secretExpiryWarning(value.clientSecretExpiresAt) && (
            <p className="sm:col-span-2 text-xs font-medium text-amber-600">
              {secretExpiryWarning(value.clientSecretExpiresAt)}
            </p>
          )}
          <label className="sm:col-span-2 flex items-center gap-2 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={value.graphSaveToSentItems}
              onChange={(e) => set('graphSaveToSentItems', e.target.checked)}
              disabled={disabled}
            />
            Kopie in „Gesendete Elemente" ablegen
          </label>
          <p className="sm:col-span-2 text-xs text-slate-500">
            Verzeichnis-ID, Anwendungs-ID und Clientschlüssel stammen aus der Entra-ID-App-Registrierung; gesendet
            wird über das unten eingetragene Postfach (Absender-Adresse). Siehe docs/mail.md für die vollständige
            Einrichtung inkl. der Exchange-Online-Einschränkung auf genau dieses Postfach.
          </p>
        </div>
      )}

      <div className={fieldGrid}>
        <Field label={value.transport === 'graph' ? 'Absender-/Sende-Postfach' : 'Absender-Adresse'}>
          <Input
            type="email"
            value={value.fromAddress}
            onChange={(e) => set('fromAddress', e.target.value)}
            placeholder="no-reply@verein.de"
            disabled={disabled}
          />
        </Field>
        <Field label="Absendername (optional)">
          <Input
            value={value.fromName}
            onChange={(e) => set('fromName', e.target.value)}
            placeholder="Badminton Manager"
            disabled={disabled}
          />
        </Field>
      </div>
    </div>
  );
}
